import { create } from 'zustand';

import questionService, { Question, SearchQuestionsParams, CreateQuestionData } from '../services/question.service';

interface QuestionState {
    questions: Question[];
    currentQuestion: Question | null;
    totalCount: number;
    loading: boolean;
    error: string | null;
    filters: SearchQuestionsParams;

    // Actions
    fetchQuestions: (filter?: SearchQuestionsParams) => Promise<void>;
    fetchQuestionById: (id: string) => Promise<void>;
    createQuestion: (data: CreateQuestionData) => Promise<Question>;
    updateQuestion: (id: string, data: Partial<CreateQuestionData>) => Promise<Question>;
    deleteQuestion: (id: string) => Promise<void>;
    cloneQuestion: (id: string) => Promise<Question>;
    setFilters: (filters: Partial<SearchQuestionsParams>) => void;
    clearError: () => void;
    reset: () => void;
}

export const useQuestionStore = create<QuestionState>((set, get) => ({
    questions: [],
    currentQuestion: null,
    totalCount: 0,
    loading: false,
    error: null,
    filters: {},

    fetchQuestions: async (filter) => {
        set({ loading: true, error: null });
        try {
            const newFilters = filter || get().filters;
            const response = await questionService.searchQuestions(newFilters);
            set({
                questions: response.questions,
                totalCount: response.total,
                filters: newFilters,
                loading: false,
            });
        } catch (error: any) {
            set({ error: error.message, loading: false });
        }
    },

    fetchQuestionById: async (id) => {
        set({ loading: true, error: null });
        try {
            const question = await questionService.getQuestion(id);
            set({ currentQuestion: question, loading: false });
        } catch (error: any) {
            set({ error: error.message, loading: false });
        }
    },

    createQuestion: async (data) => {
        set({ loading: true, error: null });
        try {
            const question = await questionService.createQuestion(data);
            set((state) => ({
                questions: [question, ...state.questions],
                totalCount: state.totalCount + 1,
                loading: false,
            }));
            return question;
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    updateQuestion: async (id, data) => {
        set({ loading: true, error: null });
        try {
            const question = await questionService.updateQuestion(id, data);
            set((state) => ({
                questions: state.questions.map((q) => (q._id === id ? question : q)),
                currentQuestion: state.currentQuestion?._id === id ? question : state.currentQuestion,
                loading: false,
            }));
            return question;
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    deleteQuestion: async (id) => {
        set({ loading: true, error: null });
        try {
            await questionService.deleteQuestion(id);
            set((state) => ({
                questions: state.questions.filter((q) => q._id !== id),
                totalCount: state.totalCount - 1,
                loading: false,
            }));
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    cloneQuestion: async (id) => {
        set({ loading: true, error: null });
        try {
            const question = await questionService.cloneQuestion(id);
            set((state) => ({
                questions: [question, ...state.questions],
                totalCount: state.totalCount + 1,
                loading: false,
            }));
            return question;
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    setFilters: (filters) => {
        set((state) => ({ filters: { ...state.filters, ...filters } }));
    },

    clearError: () => {
        set({ error: null });
    },

    reset: () => {
        set({
            questions: [],
            currentQuestion: null,
            totalCount: 0,
            loading: false,
            error: null,
            filters: {},
        });
    },
}));
